import { useState } from "react";
import { Card, Button } from "@destroy/ui";
import { LuRefreshCw, LuGlobe, LuTriangleAlert, LuShieldAlert, LuShieldCheck, LuChevronDown, LuChevronUp, LuLoader } from "react-icons/lu";
import { api } from "@/lib/api";
import { HealthScoreBadge } from "./HealthScoreBadge";
import { CheckResults } from "./CheckResults";
import type { DomainHealthCardProps, DomainHealthCheck } from "../_types";

const statusConfig = {
  healthy: {
    icon: LuShieldCheck,
    label: "Healthy",
    color: "text-[var(--color-success)]",
    bgColor: "bg-[var(--color-success)]/10",
  },
  warning: {
    icon: LuTriangleAlert,
    label: "Warning",
    color: "text-[var(--color-warning)]",
    bgColor: "bg-[var(--color-warning)]/10",
  },
  blocked: {
    icon: LuShieldAlert,
    label: "Blocked",
    color: "text-[var(--color-error)]",
    bgColor: "bg-[var(--color-error)]/10",
  },
  unknown: {
    icon: LuGlobe,
    label: "Unknown",
    color: "text-[var(--color-text-muted)]",
    bgColor: "bg-[var(--color-surface)]",
  },
};

export function DomainHealthCard({ health, onCheck, checking }: DomainHealthCardProps) {
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<DomainHealthCheck[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const config = statusConfig[health.status];
  const StatusIcon = config.icon;

  const formatLastChecked = (dateStr: string) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const handleToggleHistory = async () => {
    if (!showHistory && history.length === 0) {
      setLoadingHistory(true);
      try {
        const result = await api.domainHealth.history(health.domainId, 10);
        setHistory(result.history);
      } catch {
        // Ignore errors
      } finally {
        setLoadingHistory(false);
      }
    }
    setShowHistory(!showHistory);
  };

  return (
    <Card
      padding="md"
      className={`transition-all ${health.status === "blocked" ? "border-[var(--color-error)]/30" : health.status === "warning" ? "border-[var(--color-warning)]/30" : ""}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <HealthScoreBadge score={health.score} status={health.status} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="font-mono text-sm text-[var(--color-text)] truncate">
                {health.domain}
              </p>
              <span className={`flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded ${config.bgColor} ${config.color}`}>
                <StatusIcon className="w-3 h-3" />
                {config.label}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1 text-xs text-[var(--color-text-subtle)]">
              <span>Last checked: {formatLastChecked(health.lastChecked)}</span>
            </div>

            {/* Recommendations */}
            {health.recommendations.length > 0 && health.status !== "healthy" && (
              <div className="mt-2 space-y-1">
                {health.recommendations.map((rec, i) => (
                  <p key={i} className={`text-xs ${health.status === "blocked" ? "text-[var(--color-error)]" : "text-[var(--color-warning)]"}`}>
                    {rec}
                  </p>
                ))}
              </div>
            )}

            {/* Detailed Results */}
            <CheckResults checks={health.checks} />
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleToggleHistory}
            disabled={loadingHistory}
          >
            {loadingHistory ? (
              <LuLoader className="w-4 h-4 animate-spin" />
            ) : showHistory ? (
              <LuChevronUp className="w-4 h-4" />
            ) : (
              <LuChevronDown className="w-4 h-4" />
            )}
            History
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCheck}
            loading={checking}
          >
            <LuRefreshCw className={`w-4 h-4 ${checking ? "animate-spin" : ""}`} />
            Check
          </Button>
        </div>
      </div>

      {/* History Section */}
      {showHistory && (
        <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
          <h4 className="text-xs font-medium text-[var(--color-text-muted)] mb-2">
            Check History (Last 10)
          </h4>
          {history.length === 0 ? (
            <p className="text-xs text-[var(--color-text-subtle)]">No history available</p>
          ) : (
            <div className="space-y-2">
              {history.map((h, i) => {
                const historyConfig = statusConfig[h.status];
                return (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-4 py-1.5 px-2 rounded bg-[var(--color-elevated)]"
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${historyConfig.bgColor} ${historyConfig.color}`}>
                        {h.score}
                      </span>
                      <span className={`text-xs ${historyConfig.color}`}>
                        {historyConfig.label}
                      </span>
                    </div>
                    <span className="text-[10px] text-[var(--color-text-subtle)]">
                      {formatDate(h.lastChecked)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
